import { useEffect, useState, useRef } from 'react';
import { subscribeToMessages, getUnreadMessageCount } from '../lib/supabase/messages';
import messageSound from '../lib/soundUtils/messageSound';

/**
 * Custom hook to manage message notifications and unread count
 * This hook should be used at the top level (Navbar) to ensure it's always active
 */
export const useMessageNotifications = (userId) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(0.7);
    const [latestMessage, setLatestMessage] = useState(null); // { content, senderName, timestamp }
    const isInitialLoad = useRef(true);

    // Load saved settings
    useEffect(() => {
        const savedVolume = localStorage.getItem('messageVolume');
        const savedMuted = localStorage.getItem('messageMuted');
        if (savedVolume) setVolume(parseFloat(savedVolume));
        if (savedMuted) setIsMuted(savedMuted === 'true');
    }, []);

    // Fetch initial count and set up subscription
    useEffect(() => {
        if (!userId) return;

        // Fetch initial count
        const fetchCount = async () => {
            const count = await getUnreadMessageCount(userId);
            setUnreadCount(count);
            // After first fetch, mark init done so subsequent updates can fire toasts
            isInitialLoad.current = false;
        };

        fetchCount();

        // Subscribe to new messages
        const subscription = subscribeToMessages(userId, async (newMessage) => {
            // Immediately fetch and update count
            const count = await getUnreadMessageCount(userId);
            setUnreadCount(count);


            // Play sound
            if (!isMuted) {
                messageSound.play(volume);
            }

            // Fire toast only for genuine incoming messages (not on page load)
            if (!isInitialLoad.current) {
                setLatestMessage({
                    content: newMessage.content,
                    senderName: newMessage.sender_profile?.full_name
                        || newMessage.sender_profile?.username
                        || null,
                    timestamp: Date.now(),
                });
            }
        });

        // Poll every 30 seconds as fallback
        const interval = setInterval(async () => {
            const count = await getUnreadMessageCount(userId);
            setUnreadCount(count);
        }, 60000);

        return () => {
            if (subscription) {
                subscription.unsubscribe();
            }
            clearInterval(interval);
        };
    }, [userId, isMuted, volume]);

    // Update count when settings change
    const updateSettings = (newMuted, newVolume) => {
        if (newMuted !== undefined) setIsMuted(newMuted);
        if (newVolume !== undefined) setVolume(newVolume);
    };

    // Manual refresh function
    const refreshCount = async () => {
        if (!userId) return;
        const count = await getUnreadMessageCount(userId);
        setUnreadCount(count);
    };

    // Dismiss the toast bubble
    const dismissLatestMessage = () => setLatestMessage(null);

    return {
        unreadCount,
        refreshCount,
        updateSettings,
        latestMessage,
        dismissLatestMessage,
    };
};
